import { Injectable } from '@angular/core';
import { signalRConfig } from '@app/app.constants';
import { environment } from '@env/environment';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject, fromEvent } from 'rxjs';

const JOIN_TIMEOUT_MS = 10_000;

@Injectable({
    providedIn: 'root',
})
export class SignalRService {
    private readonly _signalRUrl = environment.signalRUrl;
    private _hubConnection!: signalR.HubConnection;
    private _signalRConnectionStatus$ = new BehaviorSubject<boolean>(false);
    private _eventSubjects: Map<string, Subject<any>> = new Map();
    private _registeredEvents: Set<string> = new Set();
    private _isConnecting = false;
    private _retryTimer: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        this.startConnection();
        this._setupBrowserHandlers();
    }

    private async startConnection(): Promise<void> {
        if (this._isConnecting) return;
        const currentState = this._hubConnection?.state;
        if (
            currentState === signalR.HubConnectionState.Connected ||
            currentState === signalR.HubConnectionState.Connecting ||
            currentState === signalR.HubConnectionState.Reconnecting
        ) {
            return;
        }

        this._isConnecting = true;
        this._clearRetryTimer();
        await this._disposeConnection();

        this._hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(this._signalRUrl, { withCredentials: false })
            .configureLogging(environment.production ? signalR.LogLevel.Error : signalR.LogLevel.Information)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    if (retryContext.elapsedMilliseconds < 60_000) return 2_000;
                    if (retryContext.elapsedMilliseconds < 300_000) return 10_000;
                    return 30_000;
                },
            })
            .build();

        const connection = this._hubConnection;
        connection.serverTimeoutInMilliseconds = 30_000;
        connection.keepAliveIntervalInMilliseconds = 15_000;

        // A replaced connection can still fire late callbacks; only the current one may change the status.
        connection.onreconnected(() => {
            if (connection === this._hubConnection) void this._onConnected(connection);
        });

        connection.onreconnecting(() => {
            if (connection === this._hubConnection) this._signalRConnectionStatus$.next(false);
        });

        connection.onclose(() => {
            if (connection !== this._hubConnection) return;
            this._signalRConnectionStatus$.next(false);
            this.retryConnection();
        });

        try {
            await connection.start();
            await this._onConnected(connection);
        } catch {
            this.retryConnection();
        } finally {
            this._isConnecting = false;
        }
    }

    private async _onConnected(connection: signalR.HubConnection): Promise<void> {
        this._reregisterEvents();
        const joined = await this._joinGroup(connection);
        if (connection !== this._hubConnection) return;
        if (joined) {
            this._signalRConnectionStatus$.next(true);
            return;
        }
        // Connected but outside the group, no broadcasts would ever arrive. Report disconnected so
        // News Desk polls, and start over (stopping triggers onclose, which schedules the retry).
        this._signalRConnectionStatus$.next(false);
        await connection.stop().catch(() => {});
    }

    private async _joinGroup(connection: signalR.HubConnection): Promise<boolean> {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const timedOut = new Promise<boolean>(resolve => {
            timer = setTimeout(() => resolve(false), JOIN_TIMEOUT_MS);
        });
        const joined = connection
            .invoke(signalRConfig.invokeEvents.joinGroup, environment.signalRGroup)
            .then(() => true, () => false);
        try {
            return await Promise.race([joined, timedOut]);
        } finally {
            clearTimeout(timer);
        }
    }

    // After a Wi-Fi switch, VPN change or wake the old socket may be dead yet still look open.
    private async _restartConnection(): Promise<void> {
        if (this._isConnecting) return;
        await this._disposeConnection();
        await this.startConnection();
    }

    private async _disposeConnection(): Promise<void> {
        if (!this._hubConnection) return;
        try {
            this._registeredEvents.forEach(name => this._hubConnection.off(name));
            if (this._hubConnection.state !== signalR.HubConnectionState.Disconnected) {
                await this._hubConnection.stop();
            }
        } catch {
            // Discarding this connection; any error here is not actionable.
        }
    }

    private _reregisterEvents(): void {
        this._registeredEvents.forEach(eventName => {
            if (!this._eventSubjects.has(eventName)) return;
            this._hubConnection.off(eventName);
            this._hubConnection.on(eventName, (data: any) => {
                this._eventSubjects.get(eventName)?.next(data);
            });
        });
    }

    private _setupBrowserHandlers(): void {
        fromEvent(document, 'visibilitychange').subscribe(() => {
            if (!document.hidden && this._hubConnection?.state !== signalR.HubConnectionState.Connected) {
                this.startConnection();
            }
        });
        fromEvent(window, 'online').subscribe(() => void this._restartConnection());
    }

    private retryConnection(): void {
        this._signalRConnectionStatus$.next(false);
        this._clearRetryTimer();
        this._retryTimer = setTimeout(() => {
            this._retryTimer = null;
            this.startConnection();
        }, signalRConfig.retryDelay);
    }

    private _clearRetryTimer(): void {
        if (this._retryTimer !== null) {
            clearTimeout(this._retryTimer);
            this._retryTimer = null;
        }
    }

    public listenToEvent<T>(eventName: string): Observable<T> {
        if (!this._eventSubjects.has(eventName)) {
            this._eventSubjects.set(eventName, new Subject<T>());
            this._registeredEvents.add(eventName);
            if (this._hubConnection) {
                this._hubConnection.off(eventName);
                this._hubConnection.on(eventName, (data: T) => {
                    this._eventSubjects.get(eventName)?.next(data);
                });
            }
        }
        return this._eventSubjects.get(eventName)!.asObservable();
    }

    public stopListening(eventName: string): void {
        if (this._eventSubjects.has(eventName)) {
            this._hubConnection?.off(eventName);
            this._eventSubjects.get(eventName)?.complete();
            this._eventSubjects.delete(eventName);
            this._registeredEvents.delete(eventName);
        }
    }

    public invokeEvent<T>(eventName: string, data: T): void {
        if (this._hubConnection?.state !== signalR.HubConnectionState.Connected) {
            return;
        }
        void this._hubConnection.invoke(eventName, data).catch(() => {
            // The connection may drop after the state check and before invoke.
            // This error is not actionable here; reconnect/onclose handlers will recover.
        });
    }

    public get signalRConnectionStatus(): Observable<boolean> {
        return this._signalRConnectionStatus$.asObservable();
    }
}